if (!customElements.get('th-header-search')) {
  customElements.define('th-header-search', class extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.toggle = this.querySelector('.th-search__toggle');
      this.panel = this.querySelector('.th-search__panel');
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('.th-search__results');
      this.initial = this.querySelector('.th-search__initial');
      this.status = this.querySelector('.th-search__status');
      this.clear = this.querySelector('[type="reset"]');
      this.cache = new Map();
      this.sequence = 0;
      this.toggle.addEventListener('click', e => { e.preventDefault(); this.panel.hidden ? this.open() : this.close(); });
      this.querySelector('.th-search__close').addEventListener('click', () => this.close());
      this.querySelector('.th-search__shade').addEventListener('click', () => this.close());
      this.panel.addEventListener('keydown', e => {
        if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close(); }
        if (e.key === 'Tab') {
          const items = [...this.panel.querySelectorAll('a[href],button,input:not([type="hidden"])')].filter(el => el.getClientRects().length && !el.disabled);
          const first = items[0], last = items[items.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
      this.input.addEventListener('input', () => this.changed());
      this.querySelector('form').addEventListener('reset', () => { this.input.value = ''; this.changed(); this.input.focus(); });
      this.querySelector('form').addEventListener('submit', e => {
        if (!this.input.value.trim()) { e.preventDefault(); return; }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'search', search_term: this.input.value.trim() });
      });
      this.position = () => {
        const header = this.closest('.header-wrapper') || this.closest('.section-header') || this.closest('header');
        const bottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
        this.style.setProperty('--th-search-top', `${bottom}px`);
      };
      this.resize = () => { if (!this.panel.hidden) this.position(); };
      window.addEventListener('resize', this.resize);
      window.visualViewport?.addEventListener('resize', this.resize);
    }
    open() {
      document.querySelectorAll('th-header-search').forEach(el => { if (el !== this && !el.panel?.hidden) el.close(false); });
      this.position();
      this.panel.hidden = false;
      this.querySelector('.th-search__shade').hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('th-search-open');
      this.input.focus({ preventScroll: true });
      if (this.input.value.trim()) this.changed();
    }
    close(restore = true) {
      clearTimeout(this.timer);
      this.controller?.abort();
      this.sequence++;
      this.panel.hidden = true;
      this.querySelector('.th-search__shade').hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('th-search-open');
      this.results.removeAttribute('aria-busy');
      this.status.textContent = '';
      if (restore) this.toggle.focus({ preventScroll: true });
    }
    changed() {
      clearTimeout(this.timer);
      this.controller?.abort();
      const seq = ++this.sequence, term = this.input.value.trim();
      this.clear.hidden = !term;
      this.initial.hidden = !!term;
      this.results.replaceChildren();
      this.status.classList.remove('visually-hidden');
      this.status.textContent = term ? this.dataset.loading : '';
      this.results.setAttribute('aria-busy', String(!!term));
      if (term) this.timer = setTimeout(() => this.search(term, seq), 220);
    }
    async search(term, seq) {
      try {
        let markup = this.cache.get(term);
        if (!markup) {
          this.controller = new AbortController();
          const url = new URL(this.dataset.endpoint, location.origin);
          url.search = new URLSearchParams({ q: term, section_id: 'th-search-results', 'resources[type]': 'product', 'resources[limit]': '6', 'resources[options][fields]': 'title,product_type,variants.title,tag' });
          const response = await fetch(url, { signal: this.controller.signal });
          if (!response.ok) throw new Error('search');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const content = parsed.querySelector('[data-th-search-response]');
          if (!content) throw new Error('markup');
          markup = content.innerHTML;
          if (this.cache.size >= 20) this.cache.delete(this.cache.keys().next().value);
          this.cache.set(term, markup);
        }
        if (seq !== this.sequence || this.panel.hidden) return;
        this.results.innerHTML = markup;
        this.status.textContent = this.results.querySelector('[data-th-search-count]')?.textContent || '';
        this.status.classList.add('visually-hidden');
        this.results.setAttribute('aria-busy', 'false');
      } catch (error) {
        if (error.name === 'AbortError' || seq !== this.sequence) return;
        this.status.classList.remove('visually-hidden');
        this.status.textContent = this.dataset.error;
        const retry = document.createElement('button');
        retry.type = 'button'; retry.textContent = this.dataset.retry;
        retry.addEventListener('click', () => this.changed());
        this.status.append(retry);
        this.results.setAttribute('aria-busy', 'false');
      }
    }
    disconnectedCallback() {
      this.controller?.abort(); clearTimeout(this.timer);
      window.removeEventListener('resize', this.resize);
      window.visualViewport?.removeEventListener('resize', this.resize);
      if (!this.panel?.hidden) document.body.classList.remove('th-search-open');
    }
  });
}
